<template>
  <div class="container-fluid">
    <div class="row mb-4 align-items-center">
      <div class="col">
        <h2>Credit Check Whitelist</h2>
      </div>
      <div class="col-auto">
        <button class="btn btn-primary" type="primary" @click="showAddModal">
          Add New Entry
        </button>
      </div>
    </div>

    <div class="row mb-3">
      <div class="col-12">
        <input
          v-model="searchTerm"
          class="form-control"
          placeholder="Search..."
          @input="onSearch"
        />
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div v-if="isLoading">
          <animated-spinner></animated-spinner>
        </div>
        <vue-good-table
          v-else-if="rows.length > 0"
          :columns="columns"
          :rows="rows"
          :search-options="{
            enabled: true,
            externalQuery: searchTerm
          }"
          :pagination-options="{
            enabled: true,
            perPage: 10
          }"
        >
          <template slot="table-row" slot-scope="props">
            <span v-if="props.column.field == 'action'">
              <a-button
                type="danger"
                shape="circle"
                @click="showDeleteModal(props.row)"
              >
                <a-icon type="delete" />
              </a-button>
            </span>
            <span v-else-if="props.column.field == 'createdat'">
              {{ formatDate(props.row.createdat) }}
            </span>
            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>
        </vue-good-table>
        <div v-else>No data available</div>
      </div>
    </div>

    <a-modal
      v-model="addModalVisible"
      title="Add New Entry"
      @ok="submitNewEntry"
      :confirmLoading="isSubmitting"
    >
      <a-form :form="form">
        <a-form-item label="Email">
          <a-input
            v-decorator="[
              'email',
              {
                rules: [
                  { required: true, message: 'Please input an email address' },
                  {
                    type: 'email',
                    message: 'Please input a valid email address'
                  }
                ]
              }
            ]"
          />
        </a-form-item>
      </a-form>
    </a-modal>

    <a-modal
      v-model="deleteModalVisible"
      title="Confirm Deletion"
      @ok="confirmDelete"
      :confirmLoading="isDeleting"
    >
      <p>Are you sure you want to delete this entry?</p>
    </a-modal>
  </div>
</template>

<script>
import moment from "moment";
import AnimatedSpinnerLoader from "@/components/AnimatedSpinnerLoader.vue";
import OnboardingService from "@/services/onboarding.js";
import { debounce } from "lodash";
import { mapGetters } from "vuex";

export default {
  name: "CreditCheckWhitelist",
  components: {
    "animated-spinner": AnimatedSpinnerLoader
  },
  computed: {
    ...mapGetters(["getUserSession"]),
  },
  data() {
    return {
      columns: [
        {
          label: "Email",
          field: "email",
          sortable: true
        },
        {
          label: "Created At",
          field: "createdat",
          sortable: true
        },
        {
          label: "Added By",
          field: "addedby",
          sortable: true
        },
        {
          label: "Action",
          field: "action",
          sortable: false,
          width: "100px"
        }
      ],
      rows: [],
      isLoading: true,
      addModalVisible: false,
      searchTerm: "",
      deleteModalVisible: false,
      isSubmitting: false,
      isDeleting: false,
      selectedRow: null,
      form: this.$form.createForm(this)
    };
  },
  methods: {
    formatDate(value) {
      return moment(value)
        .format("MMM DD, YYYY hh:mm A")
        .toUpperCase();
    },
    onSearch: debounce(function() {
      this.fetchData();
    }, 300),
    async fetchData() {
      const HTTP = this.$httpUtils.FASTHTTP;
      let url = `/creditcheck/whitelist/list`;
      if (this.searchTerm) {
        url += `?search=${encodeURIComponent(this.searchTerm)}`;
      }
      this.isLoading = true;

      try {
        const response = await HTTP.get(url);
        this.rows = response.data.data || [];
        this.isLoading = false;
      } catch (error) {
        this.isLoading = false;
        this.$message.error("Error occurred while fetching data.");
        this.rows = []; // Ensure rows is an empty array if fetch fails
      }
    },
    showAddModal() {
      this.addModalVisible = true;
    },
    submitNewEntry() {
      this.form.validateFields(async (err, values) => {
        if (!err) {
          this.isSubmitting = true;

          try {
            await OnboardingService.post(`/creditcheck/whitelist/add`, {
              email: values.email ,
              addedby: this.getUserSession !== null ? this.getUserSession.email : ""
            })
            this.isSubmitting = false;
            this.addModalVisible = false;
            this.$message.success("New entry added successfully!");
            this.fetchData();
            this.form.resetFields();
          } catch (error) {
            this.isSubmitting = false;
            this.$message.error(
              (error &&
                error.response.data &&
                error.response.data &&
                error.response.data.detail) ||
                "Error adding new entry. Please try again."
            );
          }
        }
      });
    },
    showDeleteModal(row) {
      this.selectedRow = row;
      this.deleteModalVisible = true;
    },
    async confirmDelete() {
      if (!this.selectedRow) return;

      this.isDeleting = true;
      const HTTP = this.$httpUtils.FASTHTTP;
      let url = `/creditcheck/whitelist/delete/${this.selectedRow.id}`;

      try {
        await HTTP.delete(url);
        this.isDeleting = false;
        this.deleteModalVisible = false;
        this.$message.success("Entry deleted successfully!");
        this.fetchData();
      } catch (error) {
        this.isDeleting = false;
        this.$message.error("Error deleting entry. Please try again.");
      }
    }
  },
  mounted() {
    this.fetchData();
  }
};
</script>

<style scoped>
.vgt-table.striped tbody tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, 0.05);
}
a:hover {
  color: blue;
  text-decoration: underline;
}

.ellipsis_style {
  font-size: 30px;
  color: #1388bd;
}
.pagin_style {
  color: #85878d;
}
.action_delete {
  color: red;
}
.action_delete:hover {
  background-color: red;
  color: whitesmoke;
}
.action_update {
  color: #1388bd;
}
.action_update:hover {
  background-color: #1388bd;
  color: whitesmoke;
}
.el-input__inner {
  border-radius: 10px;
  background-color: #eff0f4;
  border-color: #eff0f4;
}
.triangle {
  width: 40%;
  margin-left: auto;
  margin-right: auto;
}
.form {
  width: 55%;
  margin-left: auto;
  margin-right: auto;
}

h4 {
  font-weight: 600;
  color: #6a707e;
  border: none;
  text-align: center;
}

.row {
  margin: 20px 0px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.button-delete {
  padding: 5px;
  color: white;
  border-color: red;
  background: red;
  border-radius: 8px;
  width: 49%;
  margin-right: -7px;
}

.button-delete:hover {
  padding: 5px;
  color: white;
  border-color: rgb(146, 1, 1);
  background: rgb(146, 1, 1);
  border-radius: 8px;
  width: 49%;
  margin-right: -7px;
}

.red-border {
  border: 2px solid red;
  width: 30px;
}

.button-cancel {
  padding: 5px;
  color: grey;
  border: 1px solid grey;
  background: white;
  width: 49%;
  border-radius: 8px;
}

.button-cancel:hover {
  padding: 5px;
  color: white;
  border: 1px solid grey;
  background-color: #85878d;
  width: 49%;
  border-radius: 8px;
}

.delete-button {
  background: none;
  border: none;
  color: red;
  cursor: pointer;
}

.delete-button:hover {
  color: darkred;
}

table th,
table th span {
  font-size: 0.7em;
}
.onboarding-page {
  height: calc(100vh - 250px);
}
.uk-offcanvas-bar {
  width: 90vw !important;
}
.action_style {
  display: flex;
  gap: 10px;
}
</style>



// WEBPACK FOOTER //
// src/components/pages/onboardingTracker/CreditCheckWhitelist.vue